import { FC } from 'react'

import {
  BankingPaymentFilter,
  BankingTransferFilter,
  PaymentsDistributionWidgetDocument,
  PaymentsDistributionWidgetQuery,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
  displayedFilters: (string | string[])[]
}

interface QueryArgsType {
  currency: string
  paymentsFilters?: BankingPaymentFilter
  transfersFilters?: BankingTransferFilter
}

const prepareData = (
  data: PaymentsDistributionWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const paidPaymentsAmount = data?.banking?.paid?.aggregate?.sum?.amountCents || 0
  const paidPaymentsCount = data?.banking?.paid?.aggregate?.count || 0
  const inProgressPaymentsAmount = data?.banking?.inProgress?.aggregate?.sum?.amountCents || 0
  const inProgressPaymentsCount = data?.banking?.inProgress?.aggregate?.count || 0
  const pendingPaymentsAmount = data?.banking?.pending?.aggregate?.sum?.amountCents || 0
  const pendingPaymentsCount = data?.banking?.pending?.aggregate?.count || 0
  const pendingTransfersAmount = data?.banking?.pendingTransfers?.aggregate?.sum?.amountCents || 0
  const pendingTransfersCount = data?.banking?.pendingTransfers?.aggregate?.count || 0
  const totalAmount =
    paidPaymentsAmount + inProgressPaymentsAmount + pendingPaymentsAmount + pendingTransfersAmount
  const totalCount =
    paidPaymentsCount + inProgressPaymentsCount + pendingPaymentsCount + pendingTransfersCount

  const groupedChartData: ChartData[] = [
    {
      status: 'paid',
      color: 'feedback-positive',
      label: `${i18n.t('banking.payments.statuses.paid')} (${paidPaymentsCount})`,
      value: paidPaymentsAmount,
      formattedValue: prepareDataArgs.amountFormatter(paidPaymentsAmount),
    },
    {
      status: 'inProgress',
      color: 'categorical-2',
      label: `${i18n.t('banking.payments.statuses.in_progress')} (${inProgressPaymentsCount})`,
      value: inProgressPaymentsAmount,
      formattedValue: prepareDataArgs.amountFormatter(inProgressPaymentsAmount),
    },
    {
      status: 'pending',
      color: 'categorical-4',
      label: `${i18n.t('banking.payments.statuses.pending')} (${pendingPaymentsCount})`,
      value: pendingPaymentsAmount,
      formattedValue: prepareDataArgs.amountFormatter(pendingPaymentsAmount),
    },
    {
      status: 'pendingToManage',
      color: 'feedback-negative',
      label: `${i18n.t('banking.payments.payments_not_managed')} (${pendingTransfersCount})`,
      value: pendingTransfersAmount,
      formattedValue: prepareDataArgs.amountFormatter(pendingTransfersAmount),
    },
  ]

  const overviewChartData: ChartData = {
    label: `${i18n.t('finance.overview.outstanding_payments.total')} (${totalCount})`,
    value: totalAmount,
    formattedValue: prepareDataArgs.amountFormatter(totalAmount),
  }

  const transfersOnly = (() => {
    const paymentsCount = paidPaymentsCount + inProgressPaymentsCount + pendingPaymentsCount
    const transfersCount = pendingTransfersCount

    return !paymentsCount && !!transfersCount
  })()

  return {
    transfersOnly,
    overviewChartData,
    groupedChartData: groupedChartData,
    amountFormatter: prepareDataArgs.amountFormatter,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildPaymentsDistributionDashboardWidget = ({
  queryArgs,
  prepareDataArgs,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
}) => {
  const PaymentsDistributionDashboardWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { navigation } = useNavigation()
    const {
      overviewChartData,
      groupedChartData,
      amountFormatter,
      displayedFilters,
      transfersOnly,
    } = data

    const { paymentsFilters } = queryArgs

    const [from, to] = paymentsFilters?.date?.between || []

    let dateQueryString = ''
    if (from && to) {
      dateQueryString = `createdAtFrom=${from}&createdAtTo=${to}`
    }

    const baseRoute = !transfersOnly
      ? navigation.finance.banking.payments.fullPath()
      : navigation.finance.banking.payments.transfers.fullPath()

    return (
      <FinanceWidget
        id="paymentsDistributionDashboard"
        title={i18n.t('finance.overview.payments.payments_by_state')}
        displayedFilters={displayedFilters}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.PIE}
        showChart
        overviewChartData={overviewChartData}
        chartDatas={groupedChartData}
        linkData={{
          url: `${baseRoute}?${dateQueryString}`,
          title: i18n.t('banking.payments.short_title'),
        }}
        emptyState={{
          icon: '💰',
          text: i18n.t('finance.overview.payments.empty_state_by_status.title'),
          description: i18n.t('finance.overview.payments.empty_state_by_status.description'),
        }}
      />
    )
  }

  return buildWidget({
    id: 'paymentsDistributionWidget',
    component: PaymentsDistributionDashboardWidget,
    document: PaymentsDistributionWidgetDocument,
    prepareData: (data: PaymentsDistributionWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: queryArgs,
  })
}
